/**
 * Attendance Excel exports + South African public-holiday helpers.
 *
 * All date-serialisation MUST use local components (getFullYear / getMonth /
 * getDate). NEVER use toISOString().split('T')[0] — that converts to UTC and
 * shifts dates backward by 1 day for users in positive-offset timezones like
 * SAST (UTC+2). This is the SAST bug we intentionally guard against here.
 */

import * as XLSX from 'xlsx';

// ---------------------------------------------------------------------------
// Date helpers (LOCAL time — never UTC)
// ---------------------------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

/** Serialize a JS Date to YYYY-MM-DD using LOCAL components. */
export const toLocalISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Parse YYYY-MM-DD as a local-midnight Date. */
export const parseISODateLocal = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Add `days` to a YYYY-MM-DD, returning YYYY-MM-DD. */
export const addDaysISO = (iso, days) => {
  const d = parseISODateLocal(iso);
  d.setDate(d.getDate() + days);
  return toLocalISODate(d);
};

/** Monday-based ISO week number for a Date (1..53). */
export const getISOWeek = (d) => {
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayNr = (target.getDay() + 6) % 7; // Mon=0..Sun=6
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
};

/** Return the Monday-anchored YYYY-MM-DD for an ISO date. */
export const weekStartISO = (iso) => {
  const d = parseISODateLocal(iso);
  const dow = d.getDay(); // 0=Sun..6=Sat
  const daysFromMon = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - daysFromMon);
  return toLocalISODate(d);
};

/** Day-name for a YYYY-MM-DD ('Monday'..'Sunday'). */
export const dayName = (iso) => {
  const d = parseISODateLocal(iso);
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
};

// ---------------------------------------------------------------------------
// South African public holidays — Gauss Easter algorithm
// ---------------------------------------------------------------------------

/**
 * Calculate Easter Sunday for a given year using Gauss's algorithm.
 * Returns a Date in LOCAL time (year, month, day).
 */
export const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

/**
 * All South African statutory public holidays for a given year, including
 * Sunday→Monday observed rule (Public Holidays Act 36 of 1994).
 * Returns [{ date: 'YYYY-MM-DD', name: '...' }].
 */
export const getSAPublicHolidays = (year) => {
  const fixed = [
    { m: 1, d: 1, name: "New Year's Day" },
    { m: 3, d: 21, name: 'Human Rights Day' },
    { m: 4, d: 27, name: 'Freedom Day' },
    { m: 5, d: 1, name: "Workers' Day" },
    { m: 6, d: 16, name: 'Youth Day' },
    { m: 8, d: 9, name: "National Women's Day" },
    { m: 9, d: 24, name: 'Heritage Day' },
    { m: 12, d: 16, name: 'Day of Reconciliation' },
    { m: 12, d: 25, name: 'Christmas Day' },
    { m: 12, d: 26, name: 'Day of Goodwill' },
  ];

  const easter = calculateEaster(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  const familyDay = new Date(easter);
  familyDay.setDate(easter.getDate() + 1);

  const list = [
    ...fixed.map(({ m, d, name }) => ({ dateObj: new Date(year, m - 1, d), name })),
    { dateObj: goodFriday, name: 'Good Friday' },
    { dateObj: familyDay, name: 'Family Day' },
  ];

  const out = [];
  for (const { dateObj, name } of list) {
    out.push({ date: toLocalISODate(dateObj), name });
    // Sunday → observed Monday
    if (dateObj.getDay() === 0) {
      const obs = new Date(dateObj);
      obs.setDate(dateObj.getDate() + 1);
      out.push({ date: toLocalISODate(obs), name: `${name} (Observed)` });
    }
  }
  out.sort((a, b) => a.date.localeCompare(b.date));
  return out;
};

/** Cache by year so multi-year exports don't recompute. */
const _phCache = new Map();
const _phSet = (year) => {
  if (!_phCache.has(year)) {
    _phCache.set(year, new Map(getSAPublicHolidays(year).map((h) => [h.date, h.name])));
  }
  return _phCache.get(year);
};

/** Parse 'HH:MM' or 'HH:MM:SS' or ISO into minutes-since-midnight (local). */
const _timeToMinutes = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (s.includes('T') || (s.includes(' ') && s.includes('-'))) {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return d.getHours() * 60 + d.getMinutes();
  }
  const parts = s.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

/**
 * A shift qualifies for the night-shift allowance when its clock-out is
 * strictly AFTER the configured cut-off time (HH:MM, local). Allowance ≤ 0
 * disables the flag.
 */
export const qualifiesForNightShift = (rules, outTime) => {
  const allowance = Number(rules?.night_shift_allowance ?? 0);
  if (!(allowance > 0)) return false;
  const cutoff = _timeToMinutes(rules?.night_shift_cutoff_time || '19:00') ?? 19 * 60;
  const outMin = _timeToMinutes(outTime);
  if (outMin == null) return false;
  return outMin > cutoff;
};

/** True if `dateStr` (YYYY-MM-DD) is a SA public holiday for `year`. */
export const isPublicHoliday = (dateStr, year) => {
  if (!dateStr) return false;
  const yr = year ?? Number(dateStr.slice(0, 4));
  return _phSet(yr).has(dateStr);
};

/** Return the PH name for `dateStr`, or '' if not a public holiday. */
export const publicHolidayName = (dateStr, year) => {
  if (!dateStr) return '';
  const yr = year ?? Number(dateStr.slice(0, 4));
  return _phSet(yr).get(dateStr) || '';
};

/** True if `dateStr` (YYYY-MM-DD) is a Sunday (local). */
export const isSunday = (dateStr) => {
  const d = parseISODateLocal(dateStr);
  return d ? d.getDay() === 0 : false;
};

// ---------------------------------------------------------------------------
// Shared row/column helpers
// ---------------------------------------------------------------------------

const num = (v) => (v == null || v === '' ? 0 : Number(v) || 0);
const round2 = (v) => Math.round(v * 100) / 100;

/** Pluck up-to-2 break pairs from a record (supports both storage formats). */
const extractBreaks = (r) => {
  const breaks = Array.isArray(r.breaks) ? r.breaks : [];
  const b1 = breaks[0] || {};
  const b2 = breaks[1] || {};
  return {
    b1_out: b1.out || r.break1_out || '',
    b1_in: b1.in || r.break1_in || '',
    b2_out: b2.out || r.break2_out || '',
    b2_in: b2.in || r.break2_in || '',
  };
};

/** Build the detailed-row for a record. */
const buildDetailRow = (r, rules = {}) => {
  const yr = Number((r.date || '').slice(0, 4));
  const ph = r.is_public_holiday || isPublicHoliday(r.date, yr);
  const sun = r.is_sunday || isSunday(r.date);
  const total = num(r.work_hours ?? r.total_hours);
  const phHrs = ph ? total : num(r.public_holiday_hours);
  const phExtraHrs = ph ? num(r.public_holiday_extra_hours) : 0;
  const sunHrs = !ph && sun ? total : num(r.sunday_hours);
  const b = extractBreaks(r);
  const outTime = r.out_time || r.clock_out || '';
  const nightQualifies = r.night_shift_qualifies != null
    ? !!r.night_shift_qualifies
    : qualifiesForNightShift(rules, outTime);
  const nightAmount = nightQualifies
    ? Number(rules?.night_shift_allowance ?? 0) || 0
    : 0;
  return [
    r.date,
    dayName(r.date),
    r.name || r.employee_name || '',
    r.employee_code || r.employee_id || '',
    r.in_time || r.clock_in || '',
    outTime,
    b.b1_out,
    b.b1_in,
    b.b2_out,
    b.b2_in,
    round2(total),
    round2(sunHrs),
    round2(phHrs),
    phExtraHrs,
    round2(nightAmount),
    r.branch || r.branch_id || '',
    r.remark || r.notes || '',
  ];
};

const DETAIL_HEADERS = [
  'Date', 'Day', 'Employee', 'Employee Code',
  'Clock In', 'Clock Out',
  'Break 1 Out', 'Break 1 In', 'Break 2 Out', 'Break 2 In',
  'Total Hours', 'Sunday Hours', 'PH Hours', 'PH Extra Hours', 'Night Shift (R)',
  'Branch', 'Notes',
];

const _autoColWidths = (headers, rows) => {
  return headers.map((h, i) => {
    let max = String(h).length;
    for (const row of rows) {
      const v = row[i];
      const w = v == null ? 0 : String(v).length;
      if (w > max) max = w;
    }
    return { wch: Math.min(Math.max(max + 2, 8), 32) };
  });
};

// ---------------------------------------------------------------------------
// REPORT 1 — Detailed, single week
// ---------------------------------------------------------------------------

/**
 * exportAttendanceToExcel(attendanceData, weekNumber, year, rules = {})
 *   Detailed per-record report for a single week.
 *   Filename: Attendance_Week{N}_{year}.xlsx
 */
export const exportAttendanceToExcel = (attendanceData, weekNumber, year, rules = {}) => {
  const rows = (attendanceData || [])
    .slice()
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map((r) => buildDetailRow(r, rules));
  const sheet = XLSX.utils.aoa_to_sheet([DETAIL_HEADERS, ...rows]);
  sheet['!cols'] = _autoColWidths(DETAIL_HEADERS, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, `Week ${weekNumber}`);
  const filename = `Attendance_Week${weekNumber}_${year}.xlsx`;
  XLSX.writeFile(wb, filename);
  return { filename, rowCount: rows.length };
};

// ---------------------------------------------------------------------------
// REPORT 2 — Summary per employee, with Gratuities
// ---------------------------------------------------------------------------

const SUMMARY_HEADERS = [
  'Employee', 'Employee Code', 'Days Worked',
  'Total Regular Hours', 'Overtime Hours',
  'Sunday Hours', 'Public Holiday Hours', 'PH Extra Hours',
  'Night Shifts', 'Night Allowance (R)',
  'Gratuities (R)',
  'Stokvel Deduction (R)', 'Stokvel Company Contribution (R)',
];

/**
 * exportAttendanceSummaryToExcel(rows, startDate, endDate, gratuitiesByEmployee = {}, rules = {})
 *   `rows` come from GET /api/attendance/period-totals — the SAME server
 *   calculation the staff payroll-period screen uses. No hours maths here.
 *   Filename: Attendance_Summary_{start}_to_{end}.xlsx
 */
export const exportAttendanceSummaryToExcel = (
  periodRows,
  startDate,
  endDate,
  gratuitiesByEmployee = {},
  rules = {},
  stokvelByUser = {},
  format = 'xlsx',
) => {
  const nightRate = Number(rules?.night_shift_allowance ?? 0) || 0;
  // Merge accounts that share an employee_code (re-added staff) into one row.
  const byKey = new Map();
  for (const r of periodRows || []) {
    const key = (r.employee_code || '').toString().trim() || r.user_id;
    const g = num(gratuitiesByEmployee[r.user_id]);
    const sk = stokvelByUser[r.user_id] || {};
    const cur = byKey.get(key);
    if (!cur) {
      byKey.set(key, { ...r, grat: g, skDed: num(sk.deduction), skCo: num(sk.company) });
      continue;
    }
    cur.skDed += num(sk.deduction);
    cur.skCo += num(sk.company);
    for (const k of ['days_worked', 'ordinary', 'overtime', 'sunday', 'public_holiday',
      'public_holiday_extra', 'night_shifts']) cur[k] = num(cur[k]) + num(r[k]);
    cur.grat += g;
  }
  const rows = [];
  const totals = {
    days: 0, regular: 0, overtime: 0, sunday: 0, ph: 0, phExtra: 0,
    nightShifts: 0, nightR: 0, grat: 0, skDed: 0, skCo: 0,
  };
  const sorted = [...byKey.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  for (const e of sorted) {
    const nightR = round2(num(e.night_shifts) * nightRate);
    const grat = round2(e.grat);
    rows.push([
      e.name || e.user_id,
      e.employee_code || '',
      num(e.days_worked),
      round2(num(e.ordinary)),
      round2(num(e.overtime)),
      round2(num(e.sunday)),
      round2(num(e.public_holiday)),
      round2(num(e.public_holiday_extra)),
      num(e.night_shifts),
      nightR,
      grat,
      round2(e.skDed),
      round2(e.skCo),
    ]);
    totals.skDed += e.skDed;
    totals.skCo += e.skCo;
    totals.days += num(e.days_worked);
    totals.regular += num(e.ordinary);
    totals.overtime += num(e.overtime);
    totals.sunday += num(e.sunday);
    totals.ph += num(e.public_holiday);
    totals.phExtra += num(e.public_holiday_extra);
    totals.nightShifts += num(e.night_shifts);
    totals.nightR += nightR;
    totals.grat += grat;
  }

  const totalsRow = [
    'TOTALS', '',
    totals.days,
    round2(totals.regular),
    round2(totals.overtime),
    round2(totals.sunday),
    round2(totals.ph),
    round2(totals.phExtra),
    totals.nightShifts,
    round2(totals.nightR),
    round2(totals.grat),
    round2(totals.skDed),
    round2(totals.skCo),
  ];
  if (format === 'simplepay') {
    // SimplePay import: one sheet, employee rows only; drop name, days worked, night shifts.
    const drop = new Set([0, 2, 8]);
    const keep = (r) => r.filter((_, i) => !drop.has(i));
    const ws = XLSX.utils.aoa_to_sheet([keep(SUMMARY_HEADERS), ...rows.map(keep)]);
    const csvName = `SimplePay_${startDate}_to_${endDate}.csv`;
    // Plain UTF-8 without BOM so the header row matches on import.
    const blob = new Blob([XLSX.utils.sheet_to_csv(ws) + '\n'], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = csvName;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    return { filename: csvName, rowCount: rows.length };
  }

  const generatedAt = new Date().toLocaleString('en-ZA');
  const aoa = [
    SUMMARY_HEADERS,
    ...rows,
    totalsRow,
    [],
    [`Report Period: ${startDate} to ${endDate}`],
    [`Generated: ${generatedAt}`],
  ];
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  sheet['!cols'] = _autoColWidths(SUMMARY_HEADERS, [...rows, totalsRow]);

  // Second sheet — column reference / methodology
  const info = [
    ['Column', 'Definition'],
    ['Employee', 'Full name of the staff member.'],
    ['Employee Code', 'SimplePay / payroll identifier.'],
    ['Days Worked', 'Distinct dates with recorded working hours in the range.'],
    ['Total Regular Hours',
      'Mon-Sat hours (excluding public holidays), less any hours moved to weekly overtime. Signed-off records only. Identical to the staff payroll-period screen.'],
    ['Overtime Hours',
      'Weekly overtime only: regular hours above 45 in a Mon-Sun week. A week crossing the period boundary is evaluated in full and its overtime is reported in the payroll month in which the week concludes (its Sunday).'],
    ['Sunday Hours',
      'Total hours recorded on any Sunday (excluding Sundays that are also public holidays — those roll up under Public Holiday Hours).'],
    ['Public Holiday Hours',
      'Total hours recorded on any South African public holiday, including the observed Monday when a fixed holiday falls on a Sunday.'],
    ['PH Extra Hours',
      'Top-up hours added on public holidays so the employee is paid the greater of (double PH worked hours) OR (double normal daily hours) per BCEA §18. Calculated as: max(0, normal_dow_hours - PH worked hours), where normal_dow_hours is the MEDIAN of the employee\'s actual work_hours on the same day-of-week over the previous 60 days (whole hours). If fewer than 3 historical samples exist, falls back to the BCEA statutory daily hours (9h for 5-day workers, 8h for 6-day workers). Both PH Hours and PH Extra Hours are typically paid at double the normal rate.'],
    ['Night Shifts',
      'Count of shifts within the range whose clock-out was strictly AFTER the configured night-shift cut-off time (typically 19:00).'],
    ['Night Allowance (R)',
      'Night Shifts × the fixed rand allowance per qualifying shift, set on the Attendance Rules tab.'],
    ['Stokvel Deduction (R)',
      'TMC Stokvel member deduction for every payroll month whose payday (25th) falls inside the export range. Deduct from net salary.'],
    ['Stokvel Company Contribution (R)',
      'Employer stokvel contribution for the same payroll months (additional benefit, paid into the Savings Stokvel account).'],
    ['Gratuities (R)',
      'Tip apportionment pulled live from /api/gratuities/report for the exact date range. Falls back to R0.00 when the Cash-Recon service is unreachable.'],
    [],
    ['Notes',
      'Public holidays are calculated with Gauss’s Easter algorithm — Good Friday = Easter − 2 and Family Day = Easter + 1. All dates use local (SAST) components, not UTC, so dates never shift by ±1 day.'],
  ];
  const infoSheet = XLSX.utils.aoa_to_sheet(info);
  infoSheet['!cols'] = [{ wch: 22 }, { wch: 90 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, 'Summary');
  XLSX.utils.book_append_sheet(wb, infoSheet, 'Report Info');
  const filename = `Attendance_Summary_${startDate}_to_${endDate}.xlsx`;
  XLSX.writeFile(wb, filename);
  return { filename, rowCount: rows.length };
};

// ---------------------------------------------------------------------------
// REPORT 3 — Detailed, custom range
// ---------------------------------------------------------------------------

/**
 * exportAttendanceRangeToExcel(attendanceData, startDate, endDate, mode='detailed')
 *   Same columns as Report 1 but for an arbitrary date range.
 *   Filename: Attendance_{start}_to_{end}.xlsx
 */
export const exportAttendanceRangeToExcel = (
  attendanceData,
  startDate,
  endDate,
  mode = 'detailed', // reserved for future ('summary') — spec keeps it detailed
  rules = {},
) => {
  const rows = (attendanceData || [])
    .filter((r) => r.date && r.date >= startDate && r.date <= endDate)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map((r) => buildDetailRow(r, rules));
  const sheet = XLSX.utils.aoa_to_sheet([DETAIL_HEADERS, ...rows]);
  sheet['!cols'] = _autoColWidths(DETAIL_HEADERS, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, mode === 'summary' ? 'Summary' : 'Detailed');
  const filename = `Attendance_${startDate}_to_${endDate}.xlsx`;
  XLSX.writeFile(wb, filename);
  return { filename, rowCount: rows.length };
};
